import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  ClipboardList, MapPin, Clock, Users, Sparkles, ArrowRight, UserCircle2,
  FileText, Download, Palette, Share2, Copy, ExternalLink,
} from "lucide-react";
import ContinuityNudgeChip from "@/components/ContinuityNudgeChip";
import { toast } from "sonner";

// Renders today's active call sheet with cast rows cross-linked to
// each actor's active continuity card. Falls back to the most recent
// published call sheet when today has none. Mounts on ProjectDashboard.
export default function TodaysCallSheet({ projectId }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareLink, setShareLink] = useState(null);   // { share_url, whatsapp_url, expires_at }

  useEffect(() => {
    let dead = false;
    setLoading(true);
    api.get(`/projects/${projectId}/callsheets/current`)
      .then(({ data }) => { if (!dead) setData(data); })
      .catch((err) => {
        if (err.response?.status !== 403) {
          toast.error(formatApiErrorDetail(err.response?.data?.detail)
                       || "Could not load call sheet");
        }
        if (!dead) setData({ callsheet: null, cast: [] });
      })
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
  }, [projectId]);

  if (loading) {
    return (
      <div className="cs-card rounded-2xl p-6" data-testid="todays-callsheet-loading">
        <div className="text-sm text-white/45 py-6 text-center">Loading call sheet…</div>
      </div>
    );
  }

  const cs = data?.callsheet;
  if (!cs) {
    return (
      <div className="cs-card rounded-2xl p-6" data-testid="todays-callsheet-empty">
        <div className="flex items-center gap-3">
          <ClipboardList size={20} className="text-white/40" />
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/50">
              Today&apos;s Call Sheet
            </div>
            <div className="text-sm text-white/60 mt-1">
              No call sheet published yet. Producers publish from{" "}
              <Link to="/attendance" className="underline hover:text-white"
                    data-testid="todays-callsheet-publish-cta">
                the Attendance page
              </Link>.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const cast = data?.cast || [];

  return (
    <div className="cs-card rounded-2xl p-6" data-testid="todays-callsheet">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">
            {data.fallback_used
              ? "Last Published Call Sheet"
              : "Today's Call Sheet"}
          </div>
          <h3 className="font-display text-2xl uppercase tracking-tight mt-1">
            {cs.shoot_date}
          </h3>
          <div className="mt-2 flex items-center flex-wrap gap-3 text-[10px] tracking-[0.22em] uppercase text-white/60">
            <span className="inline-flex items-center gap-1">
              <Clock size={11} /> Call {cs.call_time}
              {cs.wrap_time && <> · Wrap {cs.wrap_time}</>}
            </span>
            {cs.location && (
              <span className="inline-flex items-center gap-1">
                <MapPin size={11} /> {cs.location}
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              <Users size={11} /> {cast.length} cast row{cast.length === 1 ? "" : "s"}
            </span>
            <ContinuityNudgeChip projectId={projectId} />
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/p/${projectId}/branding`}
                className="text-[10px] tracking-[0.22em] uppercase text-white/60 hover:text-white border border-white/15 hover:border-white/35 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors"
                data-testid="todays-callsheet-branding">
            <Palette size={11} /> Branding
          </Link>
          <button
            type="button"
            onClick={async () => {
              try {
                const { data: blob } = await api.get(
                  `/projects/${projectId}/callsheets/current/export.pdf`,
                  { responseType: "blob" },
                );
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `callsheet_${cs.shoot_date || "today"}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 60_000);
                toast.success("Call sheet PDF downloaded");
              } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail)
                             || "Could not export PDF");
              }
            }}
            className="text-[10px] tracking-[0.22em] uppercase text-white/70 hover:text-white border border-white/15 hover:border-white/35 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors"
            data-testid="todays-callsheet-download-pdf">
            <Download size={11} /> PDF
          </button>
          <button
            type="button"
            onClick={async () => {
              try {
                const { data: resp } = await api.post(
                  `/callsheets/${cs.id}/share-link`,
                  { ttl_hours: 48 },
                );
                setShareLink(resp);
                toast.success("Share link ready · expires in 48h");
              } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail)
                             || "Could not create share link");
              }
            }}
            className="text-[10px] tracking-[0.22em] uppercase text-emerald-200/90 hover:text-emerald-100 border border-emerald-400/25 hover:border-emerald-300/55 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors"
            data-testid="todays-callsheet-share">
            <Share2 size={11} /> Share
          </button>
          <Link to="/attendance"
                className="text-[10px] tracking-[0.22em] uppercase text-white/60 hover:text-white border border-white/15 hover:border-white/35 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors"
                data-testid="todays-callsheet-manage">
            Manage <ArrowRight size={11} />
          </Link>
        </div>
      </div>

      {shareLink && (
        <ShareLinkBanner link={shareLink} onDismiss={() => setShareLink(null)} />
      )}

      {cast.length === 0 ? (
        <div className="text-xs text-white/45 py-6 text-center rounded-lg border border-dashed border-white/12"
             data-testid="todays-callsheet-nocast">
          No cast rows on this call sheet. Add them from the Attendance page so
          HODs see continuity per actor.
        </div>
      ) : (
        <div className="space-y-3" data-testid="todays-callsheet-cast">
          {cast.map((row) => (
            <CastRow key={row.id} row={row} projectId={projectId} />
          ))}
        </div>
      )}
    </div>
  );
}


function CastRow({ row, projectId }) {
  const cont = row.continuity;
  const latest = cont?.latest_snapshot;
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 sm:p-4"
         data-testid={`todays-callsheet-cast-row-${row.id}`}>
      <div className="flex items-start gap-3 flex-wrap sm:flex-nowrap">
        {/* Continuity thumbnail */}
        <div className="w-14 h-14 rounded-lg overflow-hidden bg-black/40 border border-white/10 shrink-0 flex items-center justify-center">
          {cont?.hero_photo_url ? (
            <CallSheetHero projectId={projectId}
                           cardId={cont.card_id}
                           refUrl={cont.hero_photo_url} />
          ) : (
            <UserCircle2 size={28} className="text-white/25" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <div className="text-sm font-semibold text-white">
              {row.character_name || "—"}
            </div>
            {row.actor_name && (
              <div className="text-[11px] text-white/60">
                {row.actor_name}
              </div>
            )}
            {row.user_id && (
              <span className="text-[9px] uppercase tracking-[0.22em] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300">
                Linked
              </span>
            )}
          </div>

          <div className="mt-1.5 flex items-center gap-3 flex-wrap text-[10px] tracking-[0.22em] uppercase text-white/55">
            {row.call_time && (
              <span className="inline-flex items-center gap-1">
                <Clock size={10} /> Call {row.call_time}
              </span>
            )}
            {row.on_set_time && <span>On-set {row.on_set_time}</span>}
            {row.notes && (
              <span className="text-white/45 normal-case tracking-normal">
                {row.notes}
              </span>
            )}
          </div>

          {/* Continuity block */}
          {cont ? (
            <div className="mt-2 rounded-lg border border-white/10 bg-white/[0.03] p-2.5"
                 data-testid={`todays-callsheet-continuity-${row.id}`}>
              <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-white/60">
                <Sparkles size={11} className="text-[color:var(--association-theme)]" />
                Continuity
                {cont.character_name && cont.character_name !== row.character_name && (
                  <span className="normal-case tracking-normal text-white/45">
                    · card: {cont.character_name}
                  </span>
                )}
                <Link to={`/p/${projectId}/continuity`}
                      className="ml-auto text-white/55 hover:text-white inline-flex items-center gap-0.5 normal-case tracking-normal"
                      data-testid={`todays-callsheet-view-continuity-${row.id}`}>
                  View book <ArrowRight size={10} />
                </Link>
              </div>
              {latest ? (
                <div className="mt-1.5 flex items-center flex-wrap gap-2 text-[10px] tracking-[0.22em] uppercase text-white/55">
                  <span className="px-1.5 py-0.5 rounded bg-white/8 text-white/70">
                    {latest.kind || "full"}
                  </span>
                  {latest.scene_ref && (
                    <span className="inline-flex items-center gap-1">
                      <FileText size={10} /> Sc {latest.scene_ref}
                    </span>
                  )}
                  {latest.day_ref && <span>Day {latest.day_ref}</span>}
                  {latest.location && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin size={10} /> {latest.location}
                    </span>
                  )}
                </div>
              ) : (
                <div className="mt-1.5 text-[11px] text-white/40">
                  Look card ready · no snapshot logged yet.
                </div>
              )}
              {latest?.notes && (
                <div className="mt-1.5 text-xs text-white/75 line-clamp-2 whitespace-pre-line">
                  {latest.notes}
                </div>
              )}
            </div>
          ) : row.user_id ? (
            <div className="mt-2 text-[11px] text-white/40"
                 data-testid={`todays-callsheet-nocard-${row.id}`}>
              Actor linked but no continuity card yet.{" "}
              <Link to={`/p/${projectId}/continuity`}
                    className="underline hover:text-white">
                Add one
              </Link>.
            </div>
          ) : (
            <div className="mt-2 text-[11px] text-white/35">
              Link a platform actor to auto-attach continuity.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// Small inline banner shown after a share link is minted. Gives the
// producer three actions: copy the URL, open the WhatsApp share dialog,
// or open the PDF in a new tab to sanity-check the render before
// forwarding.
function ShareLinkBanner({ link, onDismiss }) {
  const expires = link.expires_at
    ? new Date(link.expires_at).toLocaleString(undefined, {
        day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
      })
    : "soon";
  return (
    <div className="mb-4 rounded-xl border border-emerald-400/25 bg-emerald-500/[0.08] p-3 sm:p-4"
         data-testid="todays-callsheet-share-banner">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-200/90">
            Share Link · expires {expires}
          </div>
          <div className="mt-1 text-xs text-white/75 truncate max-w-[520px]"
               data-testid="todays-callsheet-share-url">
            {link.share_url}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(link.share_url);
                toast.success("Copied to clipboard");
              } catch { toast.error("Copy failed"); }
            }}
            className="text-[10px] tracking-[0.22em] uppercase text-white/80 hover:text-white border border-white/15 hover:border-white/35 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors"
            data-testid="todays-callsheet-share-copy">
            <Copy size={11} /> Copy
          </button>
          <a href={link.whatsapp_url} target="_blank" rel="noopener noreferrer"
             className="text-[10px] tracking-[0.22em] uppercase text-emerald-200 hover:text-emerald-100 border border-emerald-400/35 hover:border-emerald-300/70 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors"
             data-testid="todays-callsheet-share-whatsapp">
            <ExternalLink size={11} /> WhatsApp
          </a>
          <button type="button" onClick={onDismiss}
                  className="text-[10px] tracking-[0.22em] uppercase text-white/45 hover:text-white/80 px-2 py-1.5"
                  data-testid="todays-callsheet-share-dismiss">
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}




// Blob-fetched hero image (Bearer-auth via axios interceptor).
function CallSheetHero({ projectId, cardId, refUrl }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!refUrl) return;
    if (!refUrl.startsWith("emergent:")) {
      setSrc(refUrl);
      return;
    }
    let alive = true, objectUrl = null;
    api.get(
      `/projects/${projectId}/continuity-cards/${cardId}/photos/serve`,
      { params: { path: refUrl }, responseType: "blob" },
    ).then(({ data }) => {
      if (!alive) return;
      objectUrl = URL.createObjectURL(data);
      setSrc(objectUrl);
    }).catch(() => { if (alive) setSrc(null); });
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [projectId, cardId, refUrl]);
  if (!src) return <UserCircle2 size={28} className="text-white/25" />;
  return <img src={src} alt="hero" className="w-full h-full object-cover" />;
}
